import {useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {Button, Icon} from '@ezcater/tapas';
import {faMagnifyingGlass, faXmark} from '@fortawesome/pro-regular-svg-icons';
import {useField} from 'formik';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';
import {twJoin} from 'tailwind-merge';

import UtensilsSet from '@/assets/icons/utensils-set.svg?react';
import {
  WHAT_SUGGESTIONS,
  type WhatSuggestion,
} from '@/components/global/layout/AppBar/EventBarV2/utils/what';
import SuggestedSearchChip from '@/components/global/layout/AppBar/EventBarV2/What/SuggestedSearchChip';
import WhatTypeaheadOption from '@/components/global/layout/AppBar/EventBarV2/What/WhatTypeaheadOption';
import SearchLink from '@/components/global/layout/AppBar/SearchBar/SearchLink';
import {type PartialTypeaheadResult} from '@/components/global/layout/AppBar/SearchBar/types';
import {useTypeaheadData} from '@/components/global/layout/AppBar/SearchBar/useTypeaheadData';
import {cuisineLinks, explorationLinks} from '@/components/global/layout/AppBar/SearchBar/utils';
import TextField from '@/components/ui/TextField';
import {type FulfillmentDetailAddressInput, TypeaheadDocType} from '@/graphql/types';
import useMediaQuery from '@/hooks/useMediaQuery';
import useTracking from '@/hooks/useTracking';
import {fulfillmentDetailAddressInputToStartNewSearchAddressParams} from '@/utils/geocodedAddressResult';

export type TypeaheadSuggestion = PartialTypeaheadResult;

export type KeywordSearchSelection = TypeaheadSuggestion | WhatSuggestion;

type KeywordSearchInputProps = {
  onSelect?: (selection: KeywordSearchSelection, label: string) => void;
  onEnter?: () => void;
  layout?: 'standalone' | 'multi';
  dropdownStyle?: 'classic' | 'experiment';
};

const MAX_CHARS = 100;

const KeywordSearchInput: React.FC<KeywordSearchInputProps> = ({
  onSelect,
  onEnter,
  layout = 'standalone',
  dropdownStyle = 'classic',
}) => {
  const {t} = useTranslation('home');
  const {t: tAppBar} = useTranslation('app-bar');
  const isEzx578ExperimentStyle = dropdownStyle === 'experiment';
  const [, , {setValue: setKeyword}] = useField<string>('keyword');
  const [, , {setValue: setSuggestion}] = useField<KeywordSearchSelection | null>('suggestion');
  const [{value: address}] = useField<FulfillmentDetailAddressInput | undefined>('address');
  const {track, trackClick} = useTracking();
  const isMobile = useMediaQuery(layout === 'multi' ? '(max-width: 923px)' : '(max-width: 640px)');
  const [workingValue, setWorkingValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [focusedItemIndex, setFocusedItemIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // The typeahead query keys off the address shown in this form's address field
  // (see StartOrderFormKeywordLocation). Normalize the FulfillmentDetailAddressInput
  // (`street1`) to the shape useTypeaheadData expects (`street`) up front.
  const typeaheadAddress = useMemo(
    () =>
      address
        ? {
            street: address.street1,
            zip: address.zip,
            latitude: address.latitude,
            longitude: address.longitude,
          }
        : undefined,
    [address],
  );

  const {caterers, cuisines, diversity} = useTypeaheadData(workingValue, isOpen, typeaheadAddress);
  // The event bar's What panel lists restaurants first.
  const suggestions = isEzx578ExperimentStyle
    ? [...caterers, ...cuisines, ...diversity]
    : [...cuisines, ...diversity, ...caterers];

  useEffect(() => {
    if (!isOpen) return;
    const handler = (event: MouseEvent) => {
      const target = event.target as Node;
      const container = containerRef.current;
      const dropdown = dropdownRef.current;
      if (container?.contains(target) || dropdown?.contains(target)) return;
      setIsOpen(false);
      setFocusedItemIndex(-1);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const hasSuggestions = suggestions.length > 0;
  useEffect(() => {
    if (hasSuggestions) {
      track('home-keyword-viewed-typeahead-suggestions');
    }
  }, [hasSuggestions, track]);

  useEffect(() => {
    if (!isOpen) return;
    const update = () => {
      const el = dropdownRef.current;
      const container = containerRef.current;
      if (!el || !container) return;
      const target = isMobile || isEzx578ExperimentStyle ? container : container.parentElement;
      if (!target) return;
      const rect = target.getBoundingClientRect();
      el.style.top = `${rect.bottom + window.scrollY + 8}px`;
      el.style.left = `${rect.left + window.scrollX}px`;
      el.style.width = isEzx578ExperimentStyle && !isMobile ? '' : `${rect.width}px`;
      el.style.maxHeight = '412px';
    };
    update();
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
    };
  }, [isOpen, isMobile, isEzx578ExperimentStyle, suggestions.length, workingValue]);

  const getSelectionLabel = (selection: KeywordSearchSelection) => {
    if ('filter' in selection) return tAppBar(`eventBarV2.what.suggestions.${selection.key}`);
    // Default links (popular searches / explorations) display a translated label.
    return suggestions.includes(selection)
      ? selection.display
      : tAppBar(`search.typeAhead.links.${selection.display}`);
  };

  const handleSelect = (selection: KeywordSearchSelection) => {
    const label = getSelectionLabel(selection);
    setWorkingValue(label);
    setKeyword(label);
    setIsOpen(false);
    setFocusedItemIndex(-1);
    onSelect?.(selection, label);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const hasResults = suggestions.length > 0;
    const showSuggestedSearches = isEzx578ExperimentStyle && !hasResults;
    const items = hasResults ? suggestions : [...cuisineLinks, ...explorationLinks];
    const maxIndex = showSuggestedSearches ? WHAT_SUGGESTIONS.length : items.length;

    if (event.key === 'Enter') {
      if (focusedItemIndex >= 1) {
        event.preventDefault();
        if (showSuggestedSearches) {
          const suggestion = WHAT_SUGGESTIONS[focusedItemIndex - 1];
          if (suggestion) handleSelect(suggestion);
          return;
        }
        const item = items[focusedItemIndex - 1];
        if (item) handleSelect(item);
      } else if (onEnter) {
        event.preventDefault();
        setIsOpen(false);
        setFocusedItemIndex(-1);
        onEnter();
      }
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (maxIndex > 0) {
        setFocusedItemIndex(prev => (prev === -1 ? 1 : prev < maxIndex ? prev + 1 : 1));
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (maxIndex > 0) {
        setFocusedItemIndex(prev => (prev === -1 ? maxIndex : prev > 1 ? prev - 1 : maxIndex));
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false);
      setFocusedItemIndex(-1);
    } else if (event.key === 'Tab') {
      setIsOpen(false);
      setFocusedItemIndex(-1);
      inputRef.current?.blur();
    }
  };

  const resultsList = suggestions.length > 0 && (
    <ul role="listbox">
      {suggestions.map((suggestion, index) => (
        <li
          aria-selected={focusedItemIndex === index + 1}
          id={`keyword-option-${index + 1}`}
          key={suggestion.id}
          role="option"
        >
          <button
            className={twJoin(
              'flex w-full items-center gap-2 rounded-lg px-2 py-3 text-base/[22px] font-normal hover:bg-peppercorn-50',
              focusedItemIndex === index + 1 && 'bg-peppercorn-50',
            )}
            onClick={() => handleSelect(suggestion)}
            type="button"
          >
            {suggestion.type === TypeaheadDocType.Caterer ? (
              <Image
                alt=""
                className="h-10 w-10 shrink-0 rounded-lg border border-peppercorn-100 bg-white object-contain p-0.5"
                data-chromatic="ignore"
                height={40}
                width={40}
                src={`${__DEV__ ? 'https://www.ezcater.com' : ''}/db_images/show_img/${suggestion.logoImageId}`}
              />
            ) : (
              <UtensilsSet className="h-5 w-10 shrink-0" />
            )}
            <span className="min-w-0 truncate">{suggestion.display}</span>
          </button>
        </li>
      ))}
    </ul>
  );

  const defaultAddressParams = fulfillmentDetailAddressInputToStartNewSearchAddressParams(
    address ?? undefined,
  );

  const defaultSections = suggestions.length === 0 && (
    <div className="flex w-full justify-between gap-4 bg-white max-[640px]:flex-col">
      <div className="flex flex-1 flex-col gap-2">
        <div className="text-md font-semibold">
          {t('keywordSearchInput.dropdownPopularSearches')}
        </div>
        <ul
          className="[&_a:hover]:!bg-peppercorn-50 [&_a]:!rounded-lg [&_a]:!text-base/[22px] [&_a]:!font-normal [&_li[aria-selected=true]_a]:!bg-peppercorn-50 [&_svg]:h-5"
          role="listbox"
        >
          {cuisineLinks.map((item, index) => (
            <SearchLink
              actualIndex={index + 1}
              addressParams={defaultAddressParams}
              focusedItemIndex={focusedItemIndex}
              fulfillmentDetail={null}
              isDefault
              key={item.display}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                handleSelect(item);
              }}
              suggestion={item}
            />
          ))}
        </ul>
      </div>

      <div className="flex flex-1 flex-col gap-2">
        <div className="text-md font-semibold">{t('keywordSearchInput.dropdownExplorations')}</div>
        <ul
          className="[&_a:hover]:!bg-peppercorn-50 [&_a]:!rounded-lg [&_a]:!text-base/[22px] [&_a]:!font-normal [&_li[aria-selected=true]_a]:!bg-peppercorn-50 [&_svg]:h-5"
          role="listbox"
        >
          {explorationLinks.map((item, index) => (
            <SearchLink
              actualIndex={cuisineLinks.length + index + 1}
              addressParams={defaultAddressParams}
              focusedItemIndex={focusedItemIndex}
              fulfillmentDetail={null}
              isDefault
              key={item.display}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                handleSelect(item);
              }}
              suggestion={item}
            />
          ))}
        </ul>
      </div>

      <div className="flex-1 max-[640px]:hidden" />
    </div>
  );

  // For experiment ezx578.
  // Mirrors the event bar's What panel: suggested-search chips until the typeahead has results.
  // The best option should use the What component itself, but it's not prepared to used outside the event bar yet.
  const experimentDropdownContent =
    suggestions.length > 0 ? (
      <div
        aria-label={tAppBar('eventBarV2.what.resultsLabel')}
        className="flex flex-col"
        role="listbox"
      >
        {suggestions.map((suggestion, index) => (
          <WhatTypeaheadOption
            key={suggestion.id}
            id={`keyword-option-${index + 1}`}
            isActive={focusedItemIndex === index + 1}
            onSelect={() => handleSelect(suggestion)}
            result={suggestion}
          />
        ))}
      </div>
    ) : (
      <div
        aria-label={tAppBar('eventBarV2.what.suggestedSearchesLabel')}
        className="flex flex-wrap gap-2 p-2"
        role="listbox"
      >
        {WHAT_SUGGESTIONS.map((suggestion, index) => (
          <SuggestedSearchChip
            key={suggestion.key}
            icon={suggestion.icon}
            id={`keyword-option-${index + 1}`}
            isActive={focusedItemIndex === index + 1}
            label={tAppBar(`eventBarV2.what.suggestions.${suggestion.key}`)}
            onClick={() => handleSelect(suggestion)}
          />
        ))}
      </div>
    );

  const dropdownContent = isEzx578ExperimentStyle
    ? experimentDropdownContent
    : defaultSections || resultsList;

  return (
    <div
      data-keyword-input
      className={twJoin(
        'group/keyword flex-1 rounded-full hover:bg-[#f4f4f4] max-[640px]:relative desktop924:mr-1 desktop924:focus-within:bg-white desktop924:hover:focus-within:bg-white',
        layout === 'multi' && 'peer',
        layout === 'standalone' && 'overflow-hidden',
      )}
      ref={containerRef}
    >
      <TextField.Root
        className={twJoin(
          `h-[48px] gap-0 border-0 focus-within:border-0 focus-within:shadow-none ${layout === 'multi' ? 'desktop924:focus-within:ring-peppercorn-900 group-hover/keyword:bg-[#f4f4f4] desktop924:focus-within:bg-white desktop924:focus-within:ring-2 desktop924:group-hover/keyword:focus-within:bg-white desktop924:group-has-[[data-address-input]:focus-within]:bg-[#f4f4f4]' : 'focus-within:bg-white group-hover:bg-[#f4f4f4] group-hover:focus-within:bg-white'}`,
          layout === 'standalone'
            ? 'rounded-full py-3 pl-4 pr-2 text-center focus-within:ring-0 min-[640px]:p-0'
            : 'rounded-full px-6 focus-within:rounded-full desktop924:h-full',
        )}
      >
        <TextField.Slot>
          <Icon className="mr-3" icon={faMagnifyingGlass} size="small" />
        </TextField.Slot>
        <TextField.Input
          aria-activedescendant={
            focusedItemIndex > 0
              ? suggestions.length > 0 || isEzx578ExperimentStyle
                ? `keyword-option-${focusedItemIndex}`
                : `search-option-${focusedItemIndex}`
              : undefined
          }
          aria-expanded={isOpen && (suggestions.length > 0 || isEzx578ExperimentStyle)}
          aria-haspopup="listbox"
          className={twJoin(
            layout === 'multi'
              ? 'group-hover/keyword:bg-[#f4f4f4] desktop924:group-has-[[data-address-input]:focus-within]:bg-[#f4f4f4] desktop924:group-hover/keyword:focus:bg-white'
              : 'group-hover:bg-[#f4f4f4] group-hover:focus:bg-white',
            'truncate text-base/[22px] font-normal focus:bg-white focus:!shadow-none',
          )}
          data-testid="home-keyword-search-input"
          maxLength={MAX_CHARS}
          onChange={event => {
            setWorkingValue(event.target.value);
            setKeyword(event.target.value);
            setSuggestion(null);
            setIsOpen(true);
            setFocusedItemIndex(-1);
          }}
          onFocus={() => {
            setIsOpen(true);
            trackClick('home-keyword-search-bar-opened');
          }}
          onKeyDown={handleKeyDown}
          placeholder={t('keywordSearchInput.keywordInputPlaceholder')}
          ref={inputRef}
          role="combobox"
          value={workingValue}
        />
        {workingValue && (
          <TextField.Slot>
            <Button
              aria-label={t('keywordSearchInput.keywordInputClearAriaLabel')}
              className="rounded-full p-1"
              onClick={() => {
                setWorkingValue('');
                setKeyword('');
                setSuggestion(null);
                setFocusedItemIndex(-1);
                inputRef.current?.focus();
              }}
              type="button"
              variant="secondary"
            >
              <Icon icon={faXmark} size="xsmall" />
            </Button>
          </TextField.Slot>
        )}
      </TextField.Root>

      {isOpen &&
        dropdownContent &&
        createPortal(
          <div
            ref={dropdownRef}
            data-testid="home-keyword-search-dropdown"
            className={twJoin(
              'absolute z-50 overflow-y-auto overflow-x-hidden border border-peppercorn-100 bg-white shadow-lg',
              isEzx578ExperimentStyle ? 'w-[460px] rounded-2xl p-4' : 'rounded-xl px-5 py-6',
            )}
          >
            {dropdownContent}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default KeywordSearchInput;
